import React, { useState, useEffect } from 'react';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Button from '@material-ui/core/Button';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import ListItemText from '@material-ui/core/ListItemText';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import {
  assignAdminRoles,
  deleteAdmin,
  getAdminRoles,
  getAdminUsers,
  getAllUsers,
  upsertAdminRole,
} from '../../services/apiService';
import LoadingCircleIcon from '../../icons/LoadingCircle';
import SearchAutocomplete from '../../extras/SearchAutocomplete';
import { MODULES_MAP } from '../../constants';
import { Chip, makeStyles } from '@material-ui/core';
import { toast } from 'react-toastify';
import RolesTable from './_tables/rolesTable';
import UserTable from './_tables/userTable';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
  noLabel: {
    marginTop: theme.spacing(3),
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function RBAC() {
  const [currentTab, setCurrentTab] = useState(0);
  const [data, setData] = useState({
    users: [],
    roles: [],
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({});
  const [edit, setEdit] = useState(false);
  const [error, setError] = useState('');

  const fetchAdminDetails = async () => {
    const [users, roles] = await Promise.all([
      (async () => {
        const {
          users: { data: users },
        } = await getAdminUsers();
        return users;
      })(),
      (async () => {
        const {
          roles: { data: roles },
        } = await getAdminRoles();
        return roles;
      })(),
    ]);
    setData({ users, roles });
  };

  useEffect(() => {
    fetchAdminDetails();
  }, []);

  useEffect(() => {
    setForm({});
  }, [currentTab]);

  const handleDialog = () => {
    if (dialogOpen) {
      setEdit(false);
      setForm({});
      setError('');
    }
    setDialogOpen((prevState) => !prevState);
  };

  const handleCreate = async () => {
    try {
      if (currentTab === 0) {
        if (!form.roles || !form.user) {
          setError('Please fill the complete form.');
          return;
        }
        const formData = {
          roles: form.roles,
          userId: form.user.id,
        };
        const { user: newAdmin } = await assignAdminRoles(formData);
        const tempAdmins = [...data.users];
        const index = tempAdmins.findIndex(
          (admin, index) => admin.user.id === newAdmin.user.id
        );

        if (index > -1) tempAdmins.splice(index, 1, newAdmin);
        else tempAdmins.unshift(newAdmin);
        setData({ ...data, users: tempAdmins });
        handleDialog();
      }
      if (currentTab === 1) {
        if (!form.name || !form.modules.length) {
          setError('Please fill the complete form.');
          return;
        }
        const { role: newRole } = await upsertAdminRole(form);
        const tempRoles = [...data.roles];
        const index = tempRoles.findIndex((role) => role.id === newRole.id);
        if (index > -1) tempRoles.splice(index, 1, newRole);
        else tempRoles.unshift(newRole);
        setData({ ...data, roles: tempRoles });

        handleDialog();
      }
    } catch (e) {
      console.error(e);
      if (e.response?.data?.message) {
        setError(e.response.data.message);
      }
    }
  };

  const editHandler = (row) => {
    setEdit(true);
    if (currentTab === 0) {
      setForm({
        user: row.user,
        roles: row.roles.map((role) => role.id),
      });
      handleDialog();
    }
    if (currentTab === 1) {
      setForm({
        id: row.id,
        name: row.name,
        modules: row.modules,
      });
      handleDialog();
    }
  };

  const deleteHandler = async (row) => {
    try {
      await deleteAdmin({ userId: row.user.id });

      setData((data) => {
        const tempAdmins = [...data.users];
        const index = tempAdmins.findIndex(
          (admin) => admin.user.id === row.user.id
        );
        tempAdmins.splice(index, 1);
        return { ...data, users: tempAdmins };
      });
    } catch (e) {
      console.error(e);
      toast.error('There was an error deleting. Please try again.');
    }
  };

  return (
    <div>
      <div className='flex items-center'>
        <h1 className='user-heading text-3xl font-semibold my-5'>
          Role Based Access Control
        </h1>
      </div>
      <div className='flex gap-4 items-center mt-8 mb-4 ml-2'>
        <div
          onClick={() => setCurrentTab(0)}
          className={currentTab === 0 ? 'text-blue-500' : 'cursor-pointer'}
        >
          Users
        </div>
        <span>|</span>
        <div
          onClick={() => setCurrentTab(1)}
          className={currentTab === 1 ? 'text-blue-500' : 'cursor-pointer'}
        >
          Roles
        </div>

        <button
          className='bg-white border border-solid border-gray-400 rounded mr-2 py-2 px-4 ml-auto'
          onClick={handleDialog}
        >
          {currentTab === 0 && 'Assign New User'}
          {currentTab === 1 && 'Create New Role'}
        </button>
      </div>
      {data.users.length && data.roles.length ? (
        <>
          {currentTab === 0 && (
            <div>
              <UserTable
                rowData={data.users}
                editHandler={editHandler}
                deleteHandler={deleteHandler}
              />
            </div>
          )}
          {currentTab === 1 && (
            <div>
              <RolesTable rowData={data.roles} editHandler={editHandler} />
            </div>
          )}
        </>
      ) : (
        <div className='m-auto w-full mt-20 text-center'>
          <LoadingCircleIcon iconStyle='w-10 h-10 mr-2' />
        </div>
      )}
      {dialogOpen && (
        <RBACDialog
          handleDialog={handleDialog}
          dialogOpen={dialogOpen}
          form={form}
          setForm={setForm}
          currentTab={currentTab}
          handleCreate={handleCreate}
          data={data}
          edit={edit}
          error={error}
        />
      )}
    </div>
  );
}

function RBACDialog({
  handleDialog,
  dialogOpen,
  form,
  setForm,
  currentTab,
  handleCreate,
  data,
  edit,
  error,
}) {
  const classes = useStyles();
  const dialogContent = () => {
    switch (currentTab) {
      case 0:
        return (
          <div className='flex flex-col w-full gap-y-4'>
            <SearchAutocomplete
              required
              disabled={edit}
              value={form.user || ''}
              onValueSelected={(user) => setForm({ ...form, user: user })}
              searchApi={getAllUsers}
              responseKey='users'
              label='User'
              placeholder='Search user'
            />
            <FormControl variant='outlined'>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                value={form.roles || []}
                onChange={(e) => setForm({ ...form, roles: e.target.value })}
                input={<Input />}
                renderValue={(selected) => {
                  return selected
                    .map(
                      (selectedRole) =>
                        data.roles.find((role) => role.id === selectedRole).name
                    )
                    .join(', ');
                }}
                MenuProps={MenuProps}
              >
                {data.roles.map((role, index) => (
                  <MenuItem key={role.name + index} value={role.id}>
                    <Checkbox checked={form.roles?.indexOf(role.id) > -1} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        );
      case 1:
        return (
          <div className='flex flex-col w-full gap-y-4'>
            <TextField
              disabled={edit}
              label='Role Name'
              variant='outlined'
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <FormControl variant='outlined' style={{ maxWidth: '400px' }}>
              <InputLabel>Modules</InputLabel>
              <Select
                multiple
                value={form.modules || []}
                onChange={(e) => setForm({ ...form, modules: e.target.value })}
                input={<Input />}
                renderValue={(selected) => (
                  <div className={classes.chips}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        className={classes.chip}
                      />
                    ))}
                  </div>
                )}
                MenuProps={MenuProps}
              >
                {Object.keys(MODULES_MAP).map((module, index) => (
                  <MenuItem key={module} value={module}>
                    <Checkbox checked={form.modules?.indexOf(module) > -1} />
                    <ListItemText primary={module.toUpperCase()} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      onClose={handleDialog}
      aria-labelledby='simple-dialog-title'
      open={dialogOpen}
    >
      <DialogTitle id='simple-dialog-title'>Assign New User</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Select user to assign new admin roles.
        </DialogContentText>
        <span className='text-sm text-red-500 mb-4 inline-block'>{error}</span>
        {dialogContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialog} color='primary'>
          Cancel
        </Button>
        <Button onClick={handleCreate} color='primary'>
          {edit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
